import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Buildings, Plus, Copy, Trash, ArrowRight, Sparkle } from "@phosphor-icons/react";
import BrandColorPicker from "@/components/BrandColorPicker";
import LogoUploader from "@/components/LogoUploader";
import { useAuth } from "@/lib/auth";

/**
 * Agenzia — pagina multi-agente.
 *
 * Stati possibili:
 *  1. Nessuna agenzia & beta_locked → empty state "in arrivo"
 *  2. Nessuna agenzia & non locked → form "Crea agenzia" + form "Inserisci codice"
 *  3. Agenzia esistente, agente   → vista read-only + bottone "Esci"
 *  4. Agenzia esistente, titolare → tutto: edit dati, membri, inviti, feed XML
 */
export default function Agenzia() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get("/agency/me");
      setData(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  if (loading) {
    return (
      <AppShell title="Agenzia" breadcrumb="Agenzia">
        <div className="text-[#6B6A65] text-[13px]">Caricamento…</div>
      </AppShell>
    );
  }

  return (
    <AppShell title="La mia agenzia" breadcrumb="Agenzia">
      <header className="mb-8">
        <h1 className="text-[26px] font-semibold text-[#1C1C1A] tracking-tight">La mia agenzia</h1>
        <p className="text-[13px] text-[#6B6A65] mt-1">
          Multi-agente, feed XML aggregato, inviti.
        </p>
      </header>

      {!data?.agency ? (
        <NoAgencyState data={data} onChange={reload} />
      ) : (
        <AgencyDetails data={data} onChange={reload} />
      )}
    </AppShell>
  );
}

// ───────────── Empty state ─────────────
function NoAgencyState({ data, onChange }) {
  const betaLocked = data?.beta_locked;
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Crea agenzia */}
      <div className="bg-white border border-[#EAE7DF] rounded-2xl p-6" data-testid="agency-create-card">
        <div className="w-10 h-10 rounded-xl bg-[#F1F4ED] flex items-center justify-center mb-3">
          <Buildings size={18} weight="duotone" className="text-[#7C8B6E]" />
        </div>
        <h2 className="text-[16px] font-semibold text-[#1C1C1A]">Crea la tua agenzia</h2>
        <p className="text-[12.5px] text-[#6B6A65] mt-1 mb-4">
          Diventa <strong>titolare</strong>, inviti i tuoi agenti, gestisci tutto da un unico feed.
        </p>
        {betaLocked ? (
          <div className="text-[12.5px] bg-[#F1F4ED] border border-[#D8DECE] rounded-lg px-3.5 py-3 text-[#3F3D38] leading-relaxed" data-testid="agency-beta-locked">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#7C8B6E] mb-1.5">
              <Sparkle size={10} weight="fill" /> In arrivo
            </div>
            Al lancio del piano <strong>Agenzia</strong> (multi-agente, fino a 10 agenti): un unico brand, feed XML aggregato, condivisione contatti e immobili. Per ora Pronto è single-agent.
          </div>
        ) : (
          <CreateAgencyForm onCreated={onChange} />
        )}
      </div>

      {/* Entra con codice */}
      <div className="bg-white border border-[#EAE7DF] rounded-2xl p-6" data-testid="agency-join-card">
        <div className="w-10 h-10 rounded-xl bg-[#F1F4ED] flex items-center justify-center mb-3">
          <ArrowRight size={18} weight="duotone" className="text-[#7C8B6E]" />
        </div>
        <h2 className="text-[16px] font-semibold text-[#1C1C1A]">Sei stato invitato?</h2>
        <p className="text-[12.5px] text-[#6B6A65] mt-1 mb-4">
          Inserisci il codice ricevuto dal titolare.
        </p>
        {betaLocked ? (
          <div className="text-[12.5px] bg-[#F1F4ED] border border-[#D8DECE] rounded-lg px-3.5 py-3 text-[#3F3D38] leading-relaxed">
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#7C8B6E] mb-1.5">
              <Sparkle size={10} weight="fill" /> In arrivo
            </div>
            Quando il titolare della tua agenzia attiva il piano Team, qui inserisci il codice di invito e ti unisci al feed condiviso.
          </div>
        ) : (
          <JoinAgencyForm onJoined={onChange} />
        )}
      </div>
    </div>
  );
}

function CreateAgencyForm({ onCreated }) {
  const [name, setName] = useState("");
  const [vat, setVat] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    try {
      await api.post("/agency", { name, vat: vat || null });
      toast.success("Agenzia creata");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Nome agenzia"
        className="w-full border border-[#EAE7DF] rounded-lg px-3 py-2.5 text-[13px] focus:outline-none focus:border-[#7C8B6E]"
        data-testid="agency-name-input"
      />
      <input
        type="text"
        value={vat}
        onChange={(e) => setVat(e.target.value)}
        placeholder="P.IVA (facoltativa)"
        className="w-full border border-[#EAE7DF] rounded-lg px-3 py-2.5 text-[13px] focus:outline-none focus:border-[#7C8B6E]"
        data-testid="agency-vat-input"
      />
      <button
        type="submit"
        disabled={busy || !name.trim()}
        className="w-full bg-[#1C1C1A] text-white rounded-lg px-4 py-2.5 text-[13px] font-semibold hover:bg-black transition-colors disabled:opacity-50"
        data-testid="agency-create-btn"
      >
        {busy ? "Creazione…" : "Crea agenzia"}
      </button>
    </form>
  );
}

function JoinAgencyForm({ onJoined }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      await api.post("/agency/invites/accept", { code: code.trim() });
      toast.success("Sei entrato in agenzia");
      onJoined();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Codice non valido");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <input
        type="text"
        value={code}
        onChange={(e) => setCode(e.target.value.toUpperCase())}
        placeholder="ABC12345"
        className="w-full border border-[#EAE7DF] rounded-lg px-3 py-2.5 text-[13px] uppercase tracking-[0.18em] font-mono focus:outline-none focus:border-[#7C8B6E]"
        data-testid="agency-code-input"
      />
      <button
        type="submit"
        disabled={busy || !code.trim()}
        className="w-full border border-[#7C8B6E] text-[#7C8B6E] rounded-lg px-4 py-2.5 text-[13px] font-semibold hover:bg-[#7C8B6E] hover:text-white transition-colors disabled:opacity-50"
        data-testid="agency-join-btn"
      >
        {busy ? "Verifica…" : "Entra in agenzia"}
      </button>
    </form>
  );
}

// ───────────── Agency details ─────────────
function AgencyDetails({ data, onChange }) {
  const { agency, members, is_titolare } = data;
  return (
    <div className="space-y-6">
      <AgencyHeader agency={agency} isTitolare={is_titolare} onChange={onChange} />
      {is_titolare && <LogoBrandPanel onChange={onChange} />}
      <MembersList members={members} agencyId={agency.id} isTitolare={is_titolare} onChange={onChange} />
      {is_titolare && <InvitesPanel onChange={onChange} />}
      {is_titolare && <AgencyFeedPanel />}
      <DangerZone agency={agency} isTitolare={is_titolare} onChange={onChange} />
    </div>
  );
}

function LogoBrandPanel({ onChange }) {
  const { user, refresh } = useAuth();
  return (
    <div className="bg-white border border-[#EAE7DF] rounded-2xl p-6" data-testid="agency-logo-panel">
      <h3 className="text-[14px] font-semibold text-[#1C1C1A] mb-1">Logo agenzia</h3>
      <p className="text-[12px] text-[#6B6A65] mb-4 leading-relaxed">
        Sostituisce il watermark "Pronto" sulle foto migliorate, brochure e pagine pubbliche.
        PNG/JPG/WebP/SVG · max 2MB.
      </p>
      <LogoUploader
        value={user?.agency_logo_url || ""}
        userId={user?.user_id}
        onChange={() => { refresh?.(); onChange?.(); }}
        onRefresh={refresh}
      />
    </div>
  );
}

function AgencyHeader({ agency, isTitolare, onChange }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(agency.name || "");
  const [vat, setVat] = useState(agency.vat || "");
  const [color, setColor] = useState(agency.brand_color || "#7C8B6E");

  const save = async () => {
    try {
      await api.patch("/agency", { name, vat: vat || null, brand_color: color });
      toast.success("Aggiornata");
      setEditing(false);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="bg-white border border-[#EAE7DF] rounded-2xl p-6" data-testid="agency-header">
      {!editing ? (
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-[20px] font-semibold text-[#1C1C1A]">{agency.name}</h2>
            <div className="text-[12px] text-[#6B6A65] mt-1">
              {agency.vat && <>P.IVA: {agency.vat} · </>}
              <span className="inline-flex items-center gap-1.5">
                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: agency.brand_color || "#7C8B6E" }} />
                Brand color
              </span>
            </div>
          </div>
          {isTitolare && (
            <button
              onClick={() => setEditing(true)}
              className="text-[12px] text-[#7C8B6E] hover:underline font-medium"
              data-testid="agency-edit-btn"
            >
              Modifica
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border border-[#EAE7DF] rounded-lg px-3 py-2.5 text-[13px]"
            data-testid="agency-edit-name"
          />
          <input
            type="text"
            value={vat}
            onChange={(e) => setVat(e.target.value)}
            placeholder="P.IVA"
            className="w-full border border-[#EAE7DF] rounded-lg px-3 py-2.5 text-[13px]"
            data-testid="agency-edit-vat"
          />
          <div>
            <div className="text-[11.5px] text-[#6B6A65] mb-1.5">Colore brand</div>
            <BrandColorPicker value={color} onChange={setColor} />
          </div>
          <div className="flex gap-2">
            <button onClick={save} className="bg-[#1C1C1A] text-white rounded-lg px-4 py-2 text-[12.5px] font-semibold" data-testid="agency-save-btn">Salva</button>
            <button onClick={() => setEditing(false)} className="border border-[#EAE7DF] rounded-lg px-4 py-2 text-[12.5px]">Annulla</button>
          </div>
        </div>
      )}
    </div>
  );
}

function MembersList({ members, isTitolare, onChange }) {
  const remove = async (uid) => {
    if (!window.confirm("Rimuovere questo agente dall'agenzia?")) return;
    try {
      await api.delete(`/agency/members/${uid}`);
      toast.success("Rimosso");
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="bg-white border border-[#EAE7DF] rounded-2xl p-6" data-testid="agency-members">
      <h3 className="text-[14px] font-semibold text-[#1C1C1A] mb-3">
        Membri <span className="text-[#9CA3AF] font-normal">({members.length})</span>
      </h3>
      <div className="divide-y divide-[#EAE7DF]">
        {members.map((m) => (
          <div key={m.user_id} className="flex items-center justify-between py-2.5" data-testid={`member-${m.user_id}`}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-full bg-[#F1F4ED] flex items-center justify-center text-[11px] font-semibold text-[#7C8B6E]">
                {(m.name || m.email || "?")[0].toUpperCase()}
              </div>
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-[#1C1C1A] truncate">{m.name || "—"}</div>
                <div className="text-[11.5px] text-[#6B6A65] truncate">{m.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#7C8B6E]">
                {m.agency_role}
              </span>
              {isTitolare && m.agency_role !== "titolare" && (
                <button
                  onClick={() => remove(m.user_id)}
                  className="text-[#9CA3AF] hover:text-rose-500"
                  aria-label="Rimuovi"
                  data-testid={`member-remove-${m.user_id}`}
                >
                  <Trash size={15} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function InvitesPanel({ onChange }) {
  const [invites, setInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/agency/invites");
      setInvites(r.data.invites || []);
    } catch { /* noop */ }
  };

  useEffect(() => { reload(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/agency/invites", { email: email || null });
      setEmail("");
      toast.success("Codice generato");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id) => {
    try {
      await api.delete(`/agency/invites/${id}`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    toast.success("Codice copiato");
  };

  const active = invites.filter(i => !i.used_by);

  return (
    <div className="bg-white border border-[#EAE7DF] rounded-2xl p-6" data-testid="agency-invites">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[14px] font-semibold text-[#1C1C1A]">Inviti</h3>
        <span className="text-[11.5px] text-[#6B6A65]">Genera un codice, copialo, mandalo via WhatsApp.</span>
      </div>
      <div className="flex gap-2 mb-4">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email (facoltativa)"
          className="flex-1 border border-[#EAE7DF] rounded-lg px-3 py-2 text-[12.5px]"
          data-testid="invite-email-input"
        />
        <button
          onClick={create}
          disabled={busy}
          className="bg-[#7C8B6E] text-white rounded-lg px-4 py-2 text-[12.5px] font-semibold hover:bg-[#163029] inline-flex items-center gap-1.5"
          data-testid="invite-create-btn"
        >
          <Plus size={13} /> Genera codice
        </button>
      </div>

      {active.length === 0 ? (
        <div className="text-[12px] text-[#9CA3AF]">Nessun codice attivo.</div>
      ) : (
        <div className="divide-y divide-[#EAE7DF]">
          {active.map((inv) => (
            <div key={inv.id} className="flex items-center justify-between py-2.5" data-testid={`invite-${inv.id}`}>
              <div className="min-w-0">
                <div className="font-mono text-[14px] font-semibold tracking-[0.2em] text-[#1C1C1A]">{inv.code}</div>
                <div className="text-[11px] text-[#6B6A65]">
                  {inv.email ? `Per ${inv.email}` : "Aperto"} · scade {new Date(inv.expires_at).toLocaleDateString("it-IT")}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => copy(inv.code)}
                  className="text-[#7C8B6E] hover:text-[#163029]"
                  aria-label="Copia"
                  data-testid={`invite-copy-${inv.id}`}
                >
                  <Copy size={15} />
                </button>
                <button
                  onClick={() => revoke(inv.id)}
                  className="text-[#9CA3AF] hover:text-rose-500"
                  aria-label="Revoca"
                  data-testid={`invite-revoke-${inv.id}`}
                >
                  <Trash size={15} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function AgencyFeedPanel() {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/agency/feed-info")
      .then(r => setInfo(r.data))
      .catch(err => setError(err?.response?.data?.detail || "Non disponibile"));
  }, []);

  const copy = (url) => {
    navigator.clipboard.writeText(url);
    toast.success("URL copiato");
  };

  if (error) {
    return (
      <div className="bg-white border border-[#EAE7DF] rounded-2xl p-6" data-testid="agency-feed-locked">
        <h3 className="text-[14px] font-semibold text-[#1C1C1A] mb-2">Feed XML aggregato</h3>
        <div className="text-[12px] text-[#6B6A65]">{error}</div>
      </div>
    );
  }
  if (!info) return null;

  const portals = [
    { key: "idealista", label: "Idealista" },
    { key: "immobiliare", label: "Immobiliare.it" },
    { key: "casait", label: "Casa.it" },
    { key: "subito", label: "Subito.it" },
    { key: "generic", label: "Generico (MLS)" },
  ];

  return (
    <div className="bg-white border border-[#EAE7DF] rounded-2xl p-6" data-testid="agency-feed">
      <h3 className="text-[14px] font-semibold text-[#1C1C1A] mb-1">Feed XML aggregato</h3>
      <p className="text-[12px] text-[#6B6A65] mb-4">
        Un URL per portale, copia e incolla nel backoffice del portale.
      </p>
      <div className="space-y-2">
        {portals.map(({ key, label }) => (
          <div key={key} className="flex items-center justify-between gap-3 bg-[#FAF9F4] border border-[#EAE7DF] rounded-lg px-3 py-2" data-testid={`feed-${key}`}>
            <div className="min-w-0">
              <div className="text-[12.5px] font-semibold text-[#1C1C1A]">{label}</div>
              <div className="text-[11px] text-[#6B6A65] truncate font-mono">{info.urls[key]}</div>
            </div>
            <button
              onClick={() => copy(info.urls[key])}
              className="shrink-0 text-[11.5px] inline-flex items-center gap-1 text-[#7C8B6E] hover:text-[#163029] font-semibold"
              data-testid={`feed-copy-${key}`}
            >
              <Copy size={13} /> Copia
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function DangerZone({ isTitolare, onChange }) {
  const [showLeavePreview, setShowLeavePreview] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const openLeave = async () => {
    if (isTitolare) {
      if (!window.confirm("Eliminare l'agenzia? Tutti i membri saranno scollegati.")) return;
      setBusy(true);
      try {
        await api.delete("/agency");
        toast.success("Agenzia eliminata");
        onChange();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Errore");
      } finally {
        setBusy(false);
      }
      return;
    }
    // Agente: mostra preview rassicurante prima
    setBusy(true);
    try {
      const { data } = await api.get("/agency/leave-preview");
      setPreview(data);
      setShowLeavePreview(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  const confirmLeave = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/agency/leave");
      toast.success(data?.message || "Sei uscito dall'agenzia");
      setShowLeavePreview(false);
      onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="text-right">
      <button
        onClick={openLeave}
        disabled={busy}
        className="text-[12px] text-rose-500 hover:underline font-medium disabled:opacity-50"
        data-testid="agency-danger-btn"
      >
        {isTitolare ? "Elimina agenzia" : "Esci dall'agenzia"}
      </button>

      {showLeavePreview && preview && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setShowLeavePreview(false)}
          data-testid="agency-leave-preview-modal"
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto text-left"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-[#EDEAE2]">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#7C8B6E]">
                Stai per uscire dall&apos;Agenzia
              </div>
              <h3 className="font-editorial text-2xl text-[#1C1C1A] mt-1">
                I tuoi dati restano con te.
              </h3>
              <p className="text-[13px] text-[#3A3A36] leading-relaxed mt-2">
                {preview.reassurance}
              </p>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-[#1C1C1A] mb-2">
                  Cosa ti porti via
                </div>
                <ul className="space-y-1.5">
                  {(preview.stays_with_you || []).filter((it) => it.count > 0).map((it) => (
                    <li key={it.key} className="flex items-baseline justify-between text-[13px] text-[#1C1C1A]">
                      <span>{it.label}</span>
                      <span className="font-editorial">{it.count}</span>
                    </li>
                  ))}
                  {(preview.stays_with_you || []).filter((it) => it.count > 0).length === 0 && (
                    <li className="text-[12px] text-[#9A968B] italic">
                      Non hai ancora dati su Pronto — quando li avrai, saranno automaticamente collegati a te.
                    </li>
                  )}
                </ul>
              </div>

              {preview.plan_after && (
                <div className="bg-[#FAF8F3] border border-[#E5E2D8] rounded-lg p-3.5">
                  <div className="text-[11.5px] font-semibold text-[#1C1C1A]">Piano dopo l'uscita</div>
                  <div className="text-[12px] text-[#6B6A65] mt-1 leading-snug">
                    {preview.plan_after.note}
                  </div>
                </div>
              )}
            </div>

            <div className="p-5 border-t border-[#EDEAE2] flex items-center justify-end gap-2 bg-[#FAFAF7]">
              <button
                onClick={() => setShowLeavePreview(false)}
                disabled={busy}
                className="px-4 py-2 text-[13px] font-medium text-[#3F3D38] hover:bg-white rounded-lg"
                data-testid="agency-leave-cancel"
              >
                Annulla
              </button>
              <button
                onClick={confirmLeave}
                disabled={busy}
                className="px-4 py-2 text-[13px] font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg disabled:opacity-60"
                data-testid="agency-leave-confirm"
              >
                {busy ? "Esco..." : "Esci dall'Agenzia"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
